/**
 * MANTENIMIENTO — piezas comunes (FASE 8.2)
 *
 * Etiquetas legibles (nunca el código crudo) de estado, tipo, prioridad y
 * motivo de pendiente de partes e incidencias de mantenimiento, y el bloque
 * de evidencias: mismo almacenamiento que el resto de NeatOS, rutas propias
 * de Mantenimiento, siempre INTERNAS en 8.2.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { api, descargarConSesion, FalloApi, type EvidenciaMnt } from '../api.ts';
import { ESTADOS_INCIDENCIA_MNT, ESTADOS_PT as ESTADOS_PT_CENTRAL, estadoIncidenciaMnt, estadoPtMantenimiento } from '../estados.ts';
import { Error as ErrorCaja, Estado, fechaHora, Panel, useDatos } from './base.tsx';

type Desc = { etiqueta: string; variante: string };
const d = (etiqueta: string, variante: string): Desc => ({ etiqueta, variante });

//  UX-1C: etiqueta y tono de los estados salen del mapa central (`estados.ts`); se conserva esta forma
//  ({ etiqueta, variante = tono }) para selects e historiales.
const desdeCentral = (m: Readonly<Record<string, { label: string; tone: string }>>): Readonly<Record<string, Desc>> =>
  Object.fromEntries(Object.entries(m).map(([k, x]) => [k, d(x.label, x.tone)]));
export const ESTADOS_PT: Readonly<Record<string, Desc>> = desdeCentral(ESTADOS_PT_CENTRAL);
export const ESTADOS_IM: Readonly<Record<string, Desc>> = desdeCentral(ESTADOS_INCIDENCIA_MNT);
export const PRIORIDADES: Readonly<Record<string, Desc>> = {
  BAJA: d('Baja', 'neutra'),
  MEDIA: d('Media', 'info'),
  ALTA: d('Alta', 'aviso'),
  CRITICA: d('Crítica', 'error'),
};
export const TIPOS_PT: Readonly<Record<string, string>> = {
  PREVENTIVO: 'Preventivo',
  CORRECTIVO: 'Correctivo',
  REVISION: 'Revisión',
  INSTALACION: 'Instalación',
};
export const MOTIVOS_PENDIENTE: Readonly<Record<string, string>> = {
  ESPERA_REPUESTO: 'Espera de repuesto',
  ESPERA_CLIENTE: 'Espera del cliente',
  ESPERA_PROVEEDOR: 'Espera del proveedor',
  NUEVA_VISITA: 'Nueva visita',
  OTRO: 'Otro',
};
/** FASE 9.5-C — tipos de motivo de reapertura (el texto explicativo es obligatorio aparte). */
export const MOTIVOS_REAPERTURA: Readonly<Record<string, string>> = {
  MALA_EJECUCION: 'Mala ejecución',
  RECLAMACION: 'Reclamación',
  NUEVA_INTERVENCION: 'Nueva intervención necesaria',
  CORRECCION: 'Corrección operativa',
  OTRO: 'Otro',
};
export const RESULTADOS: Readonly<Record<string, string>> = {
  RESUELTO: 'Resuelto',
  NO_RESUELTO: 'No resuelto',
  PARCIAL: 'Parcial',
};

function Etiqueta({ mapa, valor }: { mapa: Readonly<Record<string, Desc>>; valor: string }): ReactNode {
  const x = mapa[valor] ?? { etiqueta: valor, variante: 'neutra' };
  return <span className={`etiqueta ${x.variante}`}>{x.etiqueta}</span>;
}
export const EstadoPtEtiqueta = ({ estado, size }: { estado: string; size?: 'sm' | 'md' }): ReactNode => <Estado d={estadoPtMantenimiento(estado)} size={size} />;
export const EstadoImEtiqueta = ({ estado }: { estado: string }): ReactNode => <Estado d={estadoIncidenciaMnt(estado)} />;
export const PrioridadEtiqueta = ({ prioridad }: { prioridad: string }): ReactNode => <Etiqueta mapa={PRIORIDADES} valor={prioridad} />;

/** «Lun 5 oct · 09:00» en la hora LOCAL del centro (el servidor ya la devuelve así). */
export function programacion(fecha: string | null, hora: string | null): string {
  if (fecha === null) return 'Sin programar';
  const f = new Date(`${fecha}T12:00:00Z`);
  const txt = f.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${txt}${hora === null ? '' : ` · ${hora}`}`;
}

// ---------------------------------------------------------------- evidencias

const CONTEXTOS: Readonly<Record<string, string>> = {
  ANTES: 'Antes',
  DURANTE: 'Durante',
  DESPUES: 'Después',
  DOCUMENTO: 'Documento',
  GENERAL: 'General',
};

async function abrirArchivo(id: string): Promise<void> {
  await abrirArchivoRuta(`/api/mantenimiento/evidencias/${id}/archivo`);
}

/** Abre un archivo protegido (con la sesión) en una pestaña nueva. FASE 8.4: también para el área cliente. */
export async function abrirArchivoRuta(ruta: string): Promise<void> {
  const blob = await descargarConSesion(ruta);
  if (blob === null) return;
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
}

/**
 * Evidencias de un PT (opcionalmente de una intervención), de una incidencia
 * de mantenimiento o de un activo. `rutaLista`/`rutaSubida` las fija quien
 * lo usa; el servidor decide si puede.
 */
export function EvidenciasMnt({
  titulo = 'Evidencias',
  rutaLista,
  rutaSubida,
  puedeSubir,
  puedeAnular,
  puedePublicar = false,
  motivoNoPublicable,
  contextos = ['ANTES', 'DURANTE', 'DESPUES', 'GENERAL', 'DOCUMENTO'],
  intervenciones = [],
}: {
  titulo?: string;
  rutaLista: string;
  rutaSubida: string;
  puedeSubir: boolean;
  puedeAnular: boolean;
  /** FASE 8.4 — quien gestiona puede publicar una evidencia al área cliente (sólo si la raíz también es visible allí). */
  puedePublicar?: boolean;
  /** FASE 9.1-B — por qué no se puede publicar aquí (se muestra en vez de esconder la opción). */
  motivoNoPublicable?: string;
  contextos?: readonly string[];
  intervenciones?: readonly { id: string; etiqueta: string }[];
}): ReactNode {
  const lista = useDatos(() => api.get<{ filas: EvidenciaMnt[] }>(rutaLista), [rutaLista]);
  const [contexto, setContexto] = useState(contextos[0] ?? 'GENERAL');
  const [descripcion, setDescripcion] = useState('');
  const [idIntervencion, setIdIntervencion] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [publicar, setPublicar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function subir(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (archivo === null) return;
    setEnviando(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.set('contexto', contexto);
      if (descripcion.trim() !== '') fd.set('descripcion', descripcion.trim());
      if (puedePublicar && publicar) fd.set('visibilidad', 'CLIENTE');
      fd.set('archivo', archivo);
      const sep = rutaSubida.includes('?') ? '&' : '?';
      await api.subir(idIntervencion === '' ? rutaSubida : `${rutaSubida}${sep}idIntervencion=${idIntervencion}`, fd);
      setArchivo(null);
      setDescripcion('');
      setPublicar(false);
      lista.recargar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  async function cambiarVisibilidad(id: string, visibilidad: 'INTERNA' | 'CLIENTE'): Promise<void> {
    try {
      await api.post(`/api/mantenimiento/evidencias/${id}/visibilidad`, { visibilidad });
      lista.recargar();
    } catch (e) {
      setError(e);
    }
  }

  async function anular(id: string): Promise<void> {
    const motivo = window.prompt('Motivo de la anulación');
    if (motivo === null || motivo.trim() === '') return;
    try {
      await api.post(`/api/mantenimiento/evidencias/${id}/anular`, { motivo });
      lista.recargar();
    } catch (e) {
      setError(e);
    }
  }

  return (
    <section className="panel" aria-label={titulo}>
      <div className="panel-cabecera"><h2>{titulo}</h2></div>
      <div className="panel-cuerpo">
        <Panel estado={lista}>
          {(d) => (d.filas.length === 0 ? <p className="suave">Sin evidencias todavía.</p> : (
            <ul className="lista-evidencias-mnt">
              {d.filas.map((e) => (
                <li key={e.id} className={e.estado === 'ANULADA' ? 'tenue' : undefined}>
                  <button type="button" className="enlace" onClick={() => void abrirArchivo(e.id)}>{e.nombreOriginal}</button>
                  <span className="etiqueta neutra">{CONTEXTOS[e.contexto] ?? e.contexto}</span>
                  {e.idIntervencion !== null && <span className="etiqueta info">Intervención</span>}
                  {e.estado === 'ANULADA' && <span className="etiqueta error">Anulada</span>}
                  {e.visibilidad === 'CLIENTE' && e.estado === 'ACTIVA' && <span className="etiqueta info">Visible para el cliente</span>}
                  <span className="suave pequena"> {e.subidaPor} · {fechaHora(e.subidaEn)}</span>
                  {e.descripcion !== null && <div className="pequena">{e.descripcion}</div>}
                  {puedePublicar && e.estado === 'ACTIVA' && (
                    <button type="button" className="pequeno" onClick={() => void cambiarVisibilidad(e.id, e.visibilidad === 'CLIENTE' ? 'INTERNA' : 'CLIENTE')}>
                      {e.visibilidad === 'CLIENTE' ? 'Pasar a uso interno' : 'Hacer visible para cliente'}
                    </button>
                  )}
                  {puedeAnular && e.estado === 'ACTIVA' && (
                    <button type="button" className="pequeno" onClick={() => void anular(e.id)}>Anular</button>
                  )}
                </li>
              ))}
            </ul>
          ))}
        </Panel>
        {puedeSubir && (
          <form className="form-evidencia-mnt" onSubmit={(ev) => void subir(ev)} aria-label={`Añadir a ${titulo.toLowerCase()}`}>
            <div className="campo">
              <label htmlFor={`${rutaSubida}-fichero`}>Foto o documento</label>
              <input id={`${rutaSubida}-fichero`} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment"
                onChange={(ev) => setArchivo(ev.target.files?.[0] ?? null)} />
            </div>
            <div className="campo">
              <label htmlFor={`${rutaSubida}-contexto`}>Momento</label>
              <select id={`${rutaSubida}-contexto`} value={contexto} onChange={(ev) => setContexto(ev.target.value)}>
                {contextos.map((c) => <option key={c} value={c}>{CONTEXTOS[c] ?? c}</option>)}
              </select>
            </div>
            {intervenciones.length > 0 && (
              <div className="campo">
                <label htmlFor={`${rutaSubida}-intervencion`}>Intervención</label>
                <select id={`${rutaSubida}-intervencion`} value={idIntervencion} onChange={(ev) => setIdIntervencion(ev.target.value)}>
                  <option value="">Del parte en general</option>
                  {intervenciones.map((i) => <option key={i.id} value={i.id}>{i.etiqueta}</option>)}
                </select>
              </div>
            )}
            <div className="campo">
              <label htmlFor={`${rutaSubida}-descripcion`}>Descripción (opcional)</label>
              <input id={`${rutaSubida}-descripcion`} value={descripcion} onChange={(ev) => setDescripcion(ev.target.value)} />
            </div>
            {error !== null && <ErrorCaja error={error} />}
            {/* FASE 9.1-B: elección explícita, por defecto la conservadora. El servidor revalida el permiso. */}
            <fieldset className="opciones-visibilidad">
              <legend>Visibilidad</legend>
              <label><input type="radio" name={`${rutaSubida}-visibilidad`} checked={!publicar} onChange={() => setPublicar(false)} /> Uso interno</label>
              <label><input type="radio" name={`${rutaSubida}-visibilidad`} checked={publicar} disabled={!puedePublicar} onChange={() => setPublicar(true)} /> Visible para cliente</label>
            </fieldset>
            {!puedePublicar && <p className="suave pequena">{motivoNoPublicable ?? 'Uso interno: el cliente no la ve.'}</p>}
            <button type="submit" className="principal" disabled={archivo === null || enviando}>{enviando ? 'Subiendo…' : 'Subir'}</button>
          </form>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- F12.2 · navegación con contexto

/** Desde dónde se llegó (estado de la navegación): el destino ofrece «Volver a …» a ese mismo sitio, con su URL. */
export interface Volver { readonly ruta: string; readonly texto: string }

/** `state` para un `<Link>`/`navigate` que debe poder volver aquí (ruta completa, con filtros y pestaña). */
export function useVolverAqui(texto: string): { volver: Volver } {
  const l = useLocation();
  return { volver: { ruta: `${l.pathname}${l.search}`, texto } };
}

/** «← Volver a …» si se llegó con contexto; nada si se entró directamente. */
export function EnlaceVolver(): ReactNode {
  const v = (useLocation().state as { volver?: Volver } | null)?.volver;
  if (v === undefined || typeof v.ruta !== 'string' || !v.ruta.startsWith('/')) return null;
  return <Link className="enlace-volver" to={v.ruta}>← Volver a {v.texto}</Link>;
}

/**
 * F12.2 — un 409 que lista lo que bloquea (partes de una incidencia, trabajo abierto de un activo, partes de un
 * técnico): el mensaje y, debajo, cada elemento enlazado para poder resolverlo.
 */
export function ErrorConEnlaces({ error }: { error: unknown }): ReactNode {
  const d = error instanceof FalloApi ? (error.detalles as { partes?: unknown; trabajo?: unknown } | undefined) : undefined;
  const lista = (Array.isArray(d?.partes) ? d.partes : Array.isArray(d?.trabajo) ? d.trabajo : []) as
    { id: string; codigo: string; titulo: string; tipo?: string }[];
  return (
    <>
      <ErrorCaja error={error} />
      {lista.length > 0 && (
        <ul className="lista-sin-estilo" aria-label="Lo que lo impide" style={{ marginTop: 8 }}>
          {lista.map((x) => (
            <li key={x.id}>
              <Link to={x.tipo === 'INCIDENCIA' ? `/mantenimiento/incidencias/${x.id}` : `/mantenimiento/partes/${x.id}`}>{x.titulo}</Link>{' '}
              <span className="suave pequena">{x.codigo}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
